import { useState, useEffect } from "react";
import { apiPost, apiGet } from "../lib/api";
import { COLORS } from "../lib/colors";

function DeactivateModal({ recruiter, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    await onConfirm(recruiter.id, reason);
    setLoading(false);
  };

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.4)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1000,
    }}>
      <div style={{
        backgroundColor: COLORS.surface, borderRadius: 12, padding: 28,
        maxWidth: 440, width: "90%", boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
      }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, color: COLORS.text, margin: 0 }}>
          Deactivate {recruiter.email}?
        </h3>
        <p style={{ fontSize: 13, color: COLORS.textSecondary, margin: "10px 0 16px" }}>
          {recruiter.company && recruiter.company !== "—"
            ? `${recruiter.email} from ${recruiter.company} will`
            : "They will"}{" "}
          immediately lose access to your talent pool. You can reactivate them later.
        </p>

        <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textSecondary, display: "block", marginBottom: 6 }}>
          Reason (optional)
        </label>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="e.g. No longer recruiting at this school"
          rows={3}
          style={{
            width: "100%", padding: "8px 12px", borderRadius: 8,
            border: `1px solid ${COLORS.border}`, fontSize: 13,
            fontFamily: "inherit", resize: "vertical", boxSizing: "border-box",
          }}
        />

        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={loading}
            style={{
              padding: "8px 18px", borderRadius: 8, border: `1px solid ${COLORS.border}`,
              backgroundColor: COLORS.surface, color: COLORS.text,
              fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            style={{
              padding: "8px 18px", borderRadius: 8, border: "none",
              backgroundColor: COLORS.red, color: "#fff",
              fontSize: 13, fontWeight: 600, fontFamily: "inherit",
              cursor: loading ? "wait" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "Deactivating..." : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RecruiterTable({ recruiters, token, onRefresh }) {
  const [acting, setActing] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [deactivatedRecruiters, setDeactivatedRecruiters] = useState([]);
  const [loadingDeactivated, setLoadingDeactivated] = useState(false);

  const approved = (recruiters || []).filter(r => r.status !== "revoked");
  const revokedFromList = (recruiters || []).filter(r => r.status === "revoked");

  // Fetch deactivated recruiters with context when tab switches
  useEffect(() => {
    if (activeTab === "deactivated") {
      loadDeactivated();
    }
  }, [activeTab]);

  const loadDeactivated = async () => {
    setLoadingDeactivated(true);
    try {
      const res = await apiGet("/dashboard/deactivated-recruiters", token);
      setDeactivatedRecruiters(res.deactivated_recruiters || []);
    } catch {
      // Fall back to the basic revoked list from the recruiters endpoint
      setDeactivatedRecruiters(revokedFromList.map(r => ({
        id: r.id, email: r.email, company: r.company,
        deactivated_at: null, deactivated_by: null, reason: null,
      })));
    } finally {
      setLoadingDeactivated(false);
    }
  };

  const handleDeactivate = async (id, reason) => {
    try {
      await apiPost("/dashboard/deactivate-user", { user_id: id, reason: reason || null }, token);
      setDeactivateTarget(null);
      onRefresh();
      if (activeTab === "deactivated") loadDeactivated();
    } catch (err) {
      alert(`Failed to deactivate: ${err.message}`);
    }
  };

  const handleReactivate = async (id) => {
    if (!window.confirm("Reactivate this recruiter? They will regain access to your talent pool.")) return;
    setActing(id);
    try {
      await apiPost("/dashboard/reactivate-user", { user_id: id }, token);
      onRefresh();
      if (activeTab === "deactivated") loadDeactivated();
    } catch (err) {
      alert(`Failed to reactivate: ${err.message}`);
    } finally {
      setActing(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  return (
    <div>
      {/* Tab toggle */}
      <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
        {[
          { key: "active", label: "Active", count: approved.length },
          { key: "deactivated", label: "Deactivated", count: revokedFromList.length },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: "8px 20px", fontSize: 13, fontWeight: 600,
              fontFamily: "inherit", cursor: "pointer",
              border: `1px solid ${COLORS.border}`,
              borderRadius: tab.key === "active" ? "8px 0 0 8px" : "0 8px 8px 0",
              backgroundColor: activeTab === tab.key ? COLORS.accent : COLORS.surface,
              color: activeTab === tab.key ? "#fff" : COLORS.textSecondary,
              borderRight: tab.key === "active" ? "none" : undefined,
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      {/* Active recruiters tab */}
      {activeTab === "active" && (
        <>
          {approved.length === 0 ? (
            <p style={{ fontSize: 13, color: COLORS.textMuted }}>No active recruiters.</p>
          ) : (
            <div style={{
              backgroundColor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10, overflow: "hidden",
            }}>
              <table style={{
                width: "100%", borderCollapse: "collapse",
                fontSize: 13, fontFamily: "inherit",
              }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    {["Email", "Company", "Searches", "CV Views", "CV Requests", "Approved", ""].map(h => (
                      <th key={h} style={{
                        textAlign: "left", padding: "10px 14px",
                        fontWeight: 600, color: COLORS.textSecondary, fontSize: 12,
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {approved.map((r, i) => (
                    <tr key={r.id || i} style={{
                      borderBottom: i < approved.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                    }}>
                      <td style={{ padding: "10px 14px", color: COLORS.text }}>{r.email}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.company}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.searches}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.views}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.cv_requests ?? 0}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textSecondary }}>{r.cv_approved ?? 0}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button
                          onClick={() => setDeactivateTarget(r)}
                          disabled={acting === r.id}
                          style={{
                            padding: "5px 12px", borderRadius: 6, border: "none",
                            backgroundColor: COLORS.redLight, color: COLORS.red,
                            fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                            cursor: acting === r.id ? "wait" : "pointer",
                          }}
                        >
                          Deactivate
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Deactivated recruiters tab */}
      {activeTab === "deactivated" && (
        <>
          {loadingDeactivated ? (
            <p style={{ fontSize: 13, color: COLORS.textMuted }}>Loading...</p>
          ) : deactivatedRecruiters.length === 0 && revokedFromList.length === 0 ? (
            <p style={{ fontSize: 13, color: COLORS.textMuted }}>No deactivated recruiters.</p>
          ) : (
            <div style={{
              backgroundColor: COLORS.surface,
              border: `1px solid ${COLORS.border}`,
              borderRadius: 10, overflow: "hidden",
            }}>
              <table style={{
                width: "100%", borderCollapse: "collapse",
                fontSize: 13, fontFamily: "inherit",
              }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    {["Email", "Company", "Deactivated", "By", "Reason", ""].map(h => (
                      <th key={h} style={{
                        textAlign: "left", padding: "10px 14px",
                        fontWeight: 600, color: COLORS.textSecondary, fontSize: 12,
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(deactivatedRecruiters.length > 0 ? deactivatedRecruiters : revokedFromList.map(r => ({
                    id: r.id, email: r.email, company: r.company,
                    deactivated_at: null, deactivated_by: null, reason: null,
                  }))).map((r, i, arr) => (
                    <tr key={r.id || i} style={{
                      borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.borderLight}` : "none",
                    }}>
                      <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{r.email}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{r.company || "—"}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{formatDate(r.deactivated_at)}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textMuted }}>{r.deactivated_by || "—"}</td>
                      <td style={{ padding: "10px 14px", color: COLORS.textMuted, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.reason || "—"}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button
                          onClick={() => handleReactivate(r.id)}
                          disabled={acting === r.id}
                          style={{
                            padding: "5px 12px", borderRadius: 6, border: "none",
                            backgroundColor: COLORS.greenLight, color: COLORS.green,
                            fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                            cursor: acting === r.id ? "wait" : "pointer",
                          }}
                        >
                          {acting === r.id ? "..." : "Reactivate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Deactivation confirmation modal */}
      {deactivateTarget && (
        <DeactivateModal
          recruiter={deactivateTarget}
          onConfirm={handleDeactivate}
          onCancel={() => setDeactivateTarget(null)}
        />
      )}
    </div>
  );
}
